class FileUtils {
   /**
    * Get file size in human readable format
    * @param {number} bytes - File size in bytes
    * @returns {string} Formatted file size
    */
   static formatFileSize(bytes) {
      if (bytes === 0) return "0 Bytes";
      const k = 1024;
      const sizes = ["Bytes", "KB", "MB", "GB"];
      const i = Math.floor(Math.log(bytes) / Math.log(k));
      return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
   }

   /**
    * Validate file type
    * @param {File} file - File to validate
    * @param {Array<string>} allowedTypes - Array of allowed MIME types
    * @returns {boolean} True if file type is allowed
    */
   static validateFileType(file, allowedTypes = ["image/*", "application/pdf"]) {
      return allowedTypes.some((type) => {
         if (type.endsWith("/*")) {
            const baseType = type.replace("/*", "");
            return file.type.startsWith(baseType);
         }
         return file.type === type;
      });
   }

   /**
    * Validate file size
    * @param {File} file - File to validate
    * @param {number} maxSizeMB - Maximum file size in MB
    * @returns {boolean} True if file size is within limit
    */
   static validateFileSize(file, maxSizeMB = 10) {
      const maxSizeBytes = maxSizeMB * 1024 * 1024;
      return file.size <= maxSizeBytes;
   }

   /**
    * Convert a File to a data URL (base64)
    * @param {File} file
    * @returns {Promise<string>} data URL string
    */
   static fileToDataURL(file) {
      return new Promise((resolve, reject) => {
         const reader = new FileReader();
         reader.onload = () => resolve(reader.result);
         reader.onerror = reject;
         reader.readAsDataURL(file);
      });
   }

   /**
    * Build a single image payload entry for upload-images API
    * @param {string} name logical name for the file (e.g., "truckPhoto")
    * @param {File} file File object
    * @returns {Promise<{name:string, filename:string, content_type:string, data_base64:string}>}
    */
   static async buildImagePayload(name, file) {
      const dataUrl = await FileUtils.fileToDataURL(file);
      return {
         name,
         filename: file.name,
         content_type: file.type,
         data_base64: dataUrl,
      };
   }

   /**
    * Convert a map of name -> File into an array of payload items
    * @param {Record<string, File>} filesByName
    * @returns {Promise<Array<{name:string, filename:string, content_type:string, data_base64:string}>>}
    */
   static async buildImagesPayload(filesByName) {
      const entries = Object.entries(filesByName || {}).filter(([, f]) => f instanceof File);
      const payload = [];
      for (const [name, file] of entries) {
         // eslint-disable-next-line no-await-in-loop
         const item = await FileUtils.buildImagePayload(name, file);
         payload.push(item);
      }
      return payload;
   }
}

export default FileUtils;
